// Critical Error Logger - Only logs selected critical errors
import api from '../api/api';

class CriticalLogger {
  constructor() {
    this.logQueue = [];
    this.batchSize = 5; // Increased from 3 to reduce frequency
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.isOnline = navigator.onLine;
    this.maxRetries = 2; // Limit retries to prevent accumulation
    this.retryCount = 0;
    this.isFlushInProgress = false; // Prevent concurrent flushes
    
    // Monitor network status
    window.addEventListener('online', () => { 
      this.isOnline = true;
      // Flush queued logs when back online
      if (this.logQueue.length > 0 && !this.isFlushInProgress) {
        this.flush();
      }
    });
    window.addEventListener('offline', () => { this.isOnline = false; });
    
    // Flush before page unload (critical errors only)
    window.addEventListener('beforeunload', () => {
      if (this.logQueue.length > 0) {
        this.flush(true);
      }
    });
  }

  // Only log critical errors
  critical(message, meta = {}) {
    this.addToQueue('error', message, {
      ...meta,
      severity: 'critical',
      url: window.location.href,
      user_agent: navigator.userAgent,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  // Log payment failures (business critical)
  paymentError(message, meta = {}) {
    this.addToQueue('error', `Payment Error: ${message}`, {
      ...meta,
      error_type: 'payment',
      severity: 'critical',
      url: window.location.href,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  // Log API failures. Severity is derived from the HTTP status: 4xx are expected
  // client-side responses (validation 400, not-found 404, conflict 409) and are logged
  // as warnings, not critical; 5xx and network/timeout failures (no status) are genuine
  // problems and stay critical.
  apiError(endpoint, error, meta = {}) {
    const status = meta.status_code;
    const isClientError = typeof status === 'number' && status >= 400 && status < 500;
    const severity = isClientError ? 'warning' : 'critical';
    const level = isClientError ? 'warn' : 'error';
    this.addToQueue(level, `API Error: ${endpoint}`, {
      ...meta,
      endpoint,
      error_message: error.message || error,
      error_type: 'api',
      severity,
      url: window.location.href,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  // Log form submission failures (business critical)
  formError(formName, error, meta = {}) {
    this.addToQueue('error', `Form Error: ${formName}`, {
      ...meta,
      form_name: formName,
      error_message: error.message || error,
      error_type: 'form',
      severity: 'critical',
      url: window.location.href,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  // Log authentication failures
  authError(message, meta = {}) {
    this.addToQueue('error', `Auth Error: ${message}`, {
      ...meta,
      error_type: 'authentication',
      severity: 'critical',
      url: window.location.href,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  // Log payment success events (for tracking and monitoring)
  paymentSuccess(message, meta = {}) {
    this.addToQueue('info', `Payment Success: ${message}`, {
      ...meta,
      event_type: 'payment_success',
      severity: 'info',
      url: window.location.href,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    });
      this.flush(); // Immediate flush
  }

  // Log payment status polling events (for monitoring)
  paymentStatusCheck(message, meta = {}) {
    this.addToQueue('info', `Payment Status: ${message}`, {
      ...meta,
      event_type: 'payment_status_check',
      severity: 'info',
      url: window.location.href,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  // Log payment gateway events (for monitoring)
  paymentGateway(message, meta = {}) {
    this.addToQueue('info', `Payment Gateway: ${message}`, {
      ...meta,
      event_type: 'payment_gateway',
      severity: 'info',
      url: window.location.href,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    });
    // Force immediate flush for payment gateway events (business critical)
  this.flush();

  }

  addToQueue(level, message, meta) {
    this.logQueue.push({
      level,
      message,
      data: meta
    });

    // Flush immediately for critical errors if queue is getting full
    if (this.logQueue.length >= this.batchSize) {
      this.flush();
    }
  }

  async flush(synchronous = false) {
    if (this.logQueue.length === 0 || this.isFlushInProgress) return;
    
    // Don't flush if offline (except for synchronous/page unload)
    if (!this.isOnline && !synchronous) return;
    
    this.isFlushInProgress = true;
    const logs = [...this.logQueue];
    this.logQueue = [];

    try {
      if (synchronous) {
        // Use sendBeacon for critical errors during page unload
        const data = JSON.stringify({ logs });
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon('/api/logs/frontend', blob);
      } else {
        // Regular async request with timeout
        await Promise.race([
          api.post('/logs/frontend', { logs }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Logging timeout')), 3000)
          )
        ]);
        // Reset retry count on success
        this.retryCount = 0;
      }
    } catch (error) {
      console.warn('Failed to send critical logs:', error);
      // Only re-queue if under retry limit and not page unload
      if (!synchronous && this.retryCount < this.maxRetries) {
        this.logQueue.unshift(...logs);
        this.retryCount++;
      }
      // If max retries reached, drop logs to prevent memory leak
      if (this.retryCount >= this.maxRetries) {
        console.warn('Max log retry attempts reached, dropping logs');
        this.retryCount = 0;
      }
    } finally {
      this.isFlushInProgress = false;
    }
  }

  // Manual flush method
  flushNow() {
    return this.flush();
  }

  // Disable logging if needed
  disable() {
    this.logQueue = [];
    this.addToQueue = () => {}; // No-op
  }

  // Lightweight logging for non-critical info (batched with lower priority)
  lightInfo(message, meta = {}) {
    // Only add to queue if under half batch size to avoid frequent flushes
    if (this.logQueue.length < Math.floor(this.batchSize / 2)) {
      this.addToQueue('info', message, {
        ...meta,
        severity: 'low',
        url: window.location.href,
        session_id: this.sessionId,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Check if logger is healthy (not overloaded)
  isHealthy() {
    return this.logQueue.length < this.batchSize * 2 && this.retryCount < this.maxRetries;
  }
}

// Create singleton instance
const criticalLogger = new CriticalLogger();

export default criticalLogger;